import { DependencyLifeTime, Injectable } from "@miracledevs/paradigm-web-di";
import { ChartData, ChartOptions } from "chart.js";
import { createElement, ReactElement } from "react";
import { Line } from "react-chartjs-2";
import { ContextCollection } from "../../contexts/DependencyContext";

export type ChartSize = 'small' | 'medium' | 'large';
export type ZoomMode = 'xy' | 'x' | 'y';


@Injectable({ collection: ContextCollection, lifeTime: DependencyLifeTime.Singleton })
export class ChartUnitService
{
    zoomMode: ZoomMode = "xy";
    defaultOptions: ChartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        hover: {
            mode: 'index'
        },
        plugins: {
            title: {
                display: false,
                text: "Data chart",
                position: "bottom"
            },
            tooltip: {
                enabled: true,
                mode: "index",
                intersect: false,
                callbacks: {
                    title: () => '',
                    label: () => ''
                }
            },
            legend: {
                display: true
            },
            annotation: {
                annotations: {
                    box1: {
                      // Indicates the type of annotation
                      type: 'box',
                      xMin: 0,
                      xMax: 0,
                      yMin: 0,
                      yMax: 0,
                      backgroundColor: 'rgba(254, 249, 245, 0.25)', //254, 249, 245
                      borderColor: 'rgba(254, 249, 245, 0.25)',
                      borderShadowColor: 'rgba(254, 249, 245, 0.25)'
                    },
                    line1: {
                        type: 'line',
                        yMin: 0,
                        yMax: 0,
                        borderColor: 'rgb(255, 99, 132)',
                        borderWidth: 2,
                    }
                }
            }
        },
        scales: {
            x: {
                //type: 'timeseries',
                position: "bottom",
                display: true,
                ticks: {
                    maxTicksLimit: 5,
                    callback: function (value: any)
                    {
                        if (typeof value === "number")
                        {
                            const rawValLength = value.toString();
                            const fixedValLength = value.toFixed(5).toString();
                            return fixedValLength.length < rawValLength.length ? fixedValLength : rawValLength;
                        }
                    }
                },
                title: {
                    display: true,
                    text: "Time"
                },
            },
            y: {
                display: true,
                beginAtZero: false,
                position: 'left',
                max: 0,
                ticks: {
                    maxTicksLimit: 5,
                    callback: function (value: any)
                    {
                        if (typeof value === "number")
                        {
                            const rawValLength = value.toString();
                            const fixedValLength = value.toFixed(5).toString();
                            return fixedValLength.length < rawValLength.length ? fixedValLength : rawValLength;
                        }
                    },
                },
                afterDataLimits(scale) {
                    scale.max += 0;
                    scale.min -= 0;
                },
                grid: {
                    //color: 'rgba(35,122,235,0.6)',
                    borderColor: 'rgba(35,122,235,0.6)'
                },
                title: {
                    display: true,
                    text: "Head"
                }
            },
            y1: {
                display: true,
                beginAtZero: false,
                position: 'right',
                ticks: {
                    maxTicksLimit: 5,
                    callback: function (value: any)
                    {
                        if (typeof value === "number")
                        {
                            const rawValLength = value.toString();
                            const fixedValLength = value.toFixed(5).toString();
                            return fixedValLength.length < rawValLength.length ? fixedValLength : rawValLength;
                        }
                    }
                },
                grid: {
                    //color: 'rgba(235,122,35,0.6)',
                    borderColor: 'rgba(235,122,35,0.6)'
                },
                title: {
                    display: true,
                    text: "Power"
                }
            },
            y2: {
                display: true,
                beginAtZero: false,
                position: 'right',
                ticks: {
                    maxTicksLimit: 5,
                    callback: function (value: any)
                    {
                        if (typeof value === "number")
                        {
                            const rawValLength = value.toString();
                            const fixedValLength = value.toFixed(5).toString();
                            return fixedValLength.length < rawValLength.length ? fixedValLength : rawValLength;
                        }
                    }
                },
                grid: {
                    //color: 'rgba(30, 169, 138, 1)',
                    borderColor: 'rgba(30, 169, 138, 1)'
                },
                title: {
                    display: true,
                    text: "Pump"
                }
            }
        }
    };

    setZoom(zoomMode: ZoomMode): void {
        this.zoomMode = zoomMode;
    }

    getZoom(): ZoomMode {
        return this.zoomMode;
    }

    handleKeyDown (event: KeyboardEvent): void {
        if (event.repeat) return;

        if ((event.code === "ControlLeft" || event.code === "AltLeft") && this.getZoom() === "xy") {
            this.setZoom(event.code === "ControlLeft" ? "x" : "y");
        }
    }

    handleKeyUp(event: KeyboardEvent): void {
        if (this.getZoom() !== "xy") {
            this.setZoom("xy");
            if (event.target) {
                this.cleanListeners();
                this.setEventListeners();
                const eventTarget = event.target as HTMLElement;
                eventTarget.click();
                eventTarget.focus();
            }
        };
    }

    setEventListeners(elem?: HTMLElement): void {
        const target = elem || window;
        target.addEventListener("keydown", e => this.handleKeyDown(e as KeyboardEvent), { once: true });
        target.addEventListener("keyup", e => this.handleKeyUp(e as KeyboardEvent), { once: true });
    }

    cleanListeners(elem?: HTMLElement): void {
        const target = elem || window;
        target.removeEventListener("keydown", e => this.handleKeyDown(e as KeyboardEvent));
        target.removeEventListener("keyup", e => this.handleKeyUp(e as KeyboardEvent));
    }

    setChart(series: ChartData, options: ChartOptions, size: number): ReactElement {
        return createElement(Line, {
            data: series as ChartData<"line">,
            options: options as ChartOptions<"line">,
            height: size
        })
    }
}